
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { TransactionFilter } from '@olv/ui-shared/models/merchant-transaction.model';
import {
  UserActivityParams,
  ExportFormatEnum,
  ReportsService,
  TransactionExportRequest,
  WalletExportRequest,
  LogsExportRequest,
} from '@olv/ui-shared/services/reports.service';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { MatIcon } from '@angular/material/icon';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

export interface ExportDialogData {
  tabName: string;
  trancationFilters?: TransactionFilter;
  walletFilters?: WalletExportRequest['filters'];
  logFilter?: UserActivityParams;
}

@Component({
  selector: 'app-export',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TextInputComponent,
    MatIcon,
    LoadingSpinnerComponent,
    MatTooltipModule,
    ButtonComponent
],
  templateUrl: './export.component.html',
  styleUrls: ['./export.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ExportComponent {
  private readonly dialogRef = inject(MatDialogRef<ExportComponent>);
  private readonly data = inject<ExportDialogData>(MAT_DIALOG_DATA);
  private readonly reportsService = inject(ReportsService);
  private formErrorService = inject(FormErrorService);

  readonly maxExtraEmails = 5;

  tabName = this.data.tabName;
  trancationFilters: TransactionFilter = this.data.trancationFilters ?? {};
  walletFilters: WalletExportRequest['filters'] = this.data.walletFilters ?? {};
  logFilter: UserActivityParams = this.data.logFilter ?? {};
  loading = false;
  selectedFormat: ExportFormatEnum = ExportFormatEnum.xlsx;
  ExportFormatEnum = ExportFormatEnum;

  exportEmailForm = new FormGroup({
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.email],
    }),
    extraEmails: new FormArray<FormControl<string>>([]),
  });

  get emailCtrl(): FormControl<string> {
    return this.exportEmailForm.controls.email;
  }

  get extraEmailsArray(): FormArray<FormControl<string>> {
    return this.exportEmailForm.controls.extraEmails;
  }

  get isExtraEmailsLimit(): boolean {
    return this.extraEmailsArray.length >= this.maxExtraEmails;
  }

  get exportButtonState(): 'disabled' | 'default' {
    return this.loading || this.exportEmailForm.invalid ? 'disabled' : 'default';
  }

  get addMoreButtonState(): 'disabled' | 'default' {
    return this.loading || this.isExtraEmailsLimit ? 'disabled' : 'default';
  }

  getFieldError(controlName: string): string {
    return this.formErrorService.getErrorMessage(this.exportEmailForm.get(controlName), 'Email');
  }

  getExtraEmailError(index: number): string {
    const control = this.extraEmailsArray.at(index);
    return this.formErrorService.getErrorMessage(control, 'Email');
  }

  selectFormat(fmt: ExportFormatEnum): void {
    this.selectedFormat = fmt;
  }

  addEmailField(): void {
    if (this.isExtraEmailsLimit) return;

    this.extraEmailsArray.push(
      new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.email],
      })
    );
  }

  removeEmail(i: number): void {
    this.extraEmailsArray.removeAt(i);
  }

  onExport(): void {
    if (this.loading) return;

    this.loading = true;

    const emails = [this.emailCtrl.value, ...this.extraEmailsArray.value]
      .filter((e) => typeof e === 'string' && e.trim().length > 0)
      .map((e) => e.trim());

    const onSuccess = (result: any) => {
      if (!result) return;
      this.downloadBlob(result.blob, result.filename);
      this.dialogRef.close();
    };

    const onError = () => {
      this.loading = false;
    };

    if (this.tabName === 'transaction') {
      const payload: TransactionExportRequest = {
        exportFormat: this.selectedFormat,
        filters: { ...this.trancationFilters },
        emails: emails.length ? emails : undefined,
      };

      this.reportsService.exportTransactions(payload).subscribe({
        next: onSuccess,
        error: onError,
      });
    }

    if (this.tabName === 'wallets') {
      const payload: WalletExportRequest = {
        exportFormat: this.selectedFormat,
        filters: { ...this.walletFilters },
        emails: emails.length ? emails : undefined,
      };

      this.reportsService.exportWallets(payload).subscribe({
        next: onSuccess,
        error: onError,
      });
    }

    if (this.tabName === 'userActivity') {
      const payload: LogsExportRequest = {
        exportFormat: this.selectedFormat,
        filters: { ...this.logFilter },
        emails: emails.length ? emails : undefined,
      };

      this.reportsService.exportActivityLogs(payload).subscribe({
        next: onSuccess,
        error: onError,
      });
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  trackByIndex(index: number): number {
    return index;
  }
}
